import G6, { IG6GraphEvent } from '@antv/g6';
import { GraphBehavior, GraphEdgeEvent, GraphNodeEvent, ItemState } from '../constants';
import { GroupName, getChildNodeGroupFromEvent, setChildNodeState, deleteUtils } from '../shapes';
import { Behavior } from '../types';

interface HoverItemBehaviorConfig {
  /** The state to set on hover */
  hoverState: ItemState | string;
  /**
   * Should the update be forced?
   * This can have performance implications, so it should only be run if
   * necessary.  EG: if the item needs to be resized.
   */
  shouldForceUpdateOnHover(
    item: IG6GraphEvent['item'],
    state: ItemState,
    value: string | boolean,
  ): boolean;
}

interface HoverItemBehavior extends Behavior {
  /** Update the state of an item */
  afterSetItemState(item: IG6GraphEvent['item'], state: ItemState, value: string | boolean): void;
  /** Update the state of an item */
  setItemState(item: IG6GraphEvent['item'], state: ItemState, value: string | boolean): void;
  /** Handle mouse enter */
  handleItemMouseenter(e: IG6GraphEvent): void;
  /** Handle mouse leave */
  handleItemMouseleave(e: IG6GraphEvent): void;
  /** Handle mouse entering a child node */
  handleChildNodeMouseenter(e: IG6GraphEvent): void;
  /** Handle mouse leaving a child node */
  handleChildNodeMouseleave(e: IG6GraphEvent): void;
}

const hoverItemBehavior: HoverItemBehavior = {
  getDefaultCfg(): HoverItemBehaviorConfig {
    return {
      hoverState: ItemState.Active,
      /**
       * Should the update be forced?
       * This can have performance implications, so it should only be run if
       * necessary.  EG: if the item needs to be resized.
       */
      shouldForceUpdateOnHover: () => false,
    };
  },
  getEvents() {
    return {
      [GraphNodeEvent.onNodeMouseEnter]: 'handleItemMouseenter',
      [GraphEdgeEvent.onEdgeMouseEnter]: 'handleItemMouseenter',
      [GraphNodeEvent.onNodeMouseLeave]: 'handleItemMouseleave',
      [GraphEdgeEvent.onEdgeMouseLeave]: 'handleItemMouseleave',
      [`${GroupName.ChildNode}:mouseenter`]: 'handleChildNodeMouseenter',
      [`${GroupName.ChildNode}:mouseleave`]: 'handleChildNodeMouseleave',
    };
  },
  setItemState(item, state, value) {
    if (!item) return;

    item.setState(state, value);

    /**
     * If the options specify that an update should be forced, call the update
     * function.
     *
     * This should be done if there is relayout of the item, such as on size
     * changes
     */
    if (this.shouldForceUpdateOnHover(item, state, value)) {
      item.update(item?.getModel(), 'style');
    }
    this.afterSetItemState(item, state, value);
  },

  afterSetItemState(item, state, value) {
    if (!item) return;
    if (deleteUtils.isEnabled(item?.getModel())) {
      const group = item?.getContainer();
      deleteUtils.updateVisibility({ group, isVisible: state === ItemState.Active && !!value });
    }
  },

  handleItemMouseenter(e) {
    e.item?.toFront();
    this.setItemState(e.item, this.hoverState, true);
  },

  handleItemMouseleave(e) {
    this.setItemState(e.item, this.hoverState, false);
  },

  handleChildNodeMouseenter(e) {
    const { item } = e;
    const childNode = getChildNodeGroupFromEvent(e);
    if (childNode && item) {
      setChildNodeState(childNode, this.hoverState, true);
      this.setItemState(item, this.hoverState, false);
    }
  },

  handleChildNodeMouseleave(e) {
    const { item } = e;
    const childNode = getChildNodeGroupFromEvent(e);
    if (childNode && item) {
      setChildNodeState(childNode, this.hoverState, false);
      this.setItemState(item, this.hoverState, true);
    }
  },
};

G6.registerBehavior(GraphBehavior.HoverItem, hoverItemBehavior);
